import {
  Component,
  Input,
  Output,
  EventEmitter,
  ContentChild,
  TemplateRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { AddButtonComponent } from '../add-button/add-button.component';
import { SimpleDropdownComponent } from '../simple-dropdown/simple-dropdown.component';

export interface DropdownConfig {
  options: { label: string; value: any }[];
  selectedValue: any;
  placeholder: string;
  title: string;
  disabled?: boolean;
}

/**
 * A reusable title bar component that displays a heading, optional description, and an optional action button and/or dropdown controls. It supports configurable button appearance, icons, and content projection for custom button or dropdown templates.
 * @component
 * @selector app-title-bar
 * @example
 *   <app-title-bar
 *     [title]="'Users'"
 *     [description]="'Manage application users'"
 *     [showButton]="true"
 *     [buttonText]="'Add user'"
 *     [buttonVariant]="'primary'"
 *     [buttonSize]="'medium'"
 *     (buttonClick)="onAddUser()"
 *     (dropdownChange)="onDropdownChanged($event)"
 *   ></app-title-bar>
 * @input {string} title - The main title text shown in the title bar.
 * @input {string} description - Optional descriptive text displayed below or beside the title.
 * @input {boolean} showButton - Whether to render the action button.
 * @input {string} buttonText - Text label for the action button.
 * @input {'default'|'primary'|'secondary'|'success'|'danger'} buttonVariant - Visual variant for the action button.
 * @input {'small'|'medium'|'large'} buttonSize - Size of the action button.
 * @input {string | undefined} buttonIcon - Optional icon name to render inside the button.
 * @input {string | undefined} customIcon - Optional custom icon (e.g., URL or CSS class) for the button.
 * @input {'default'|'break-settings'|'reward-settings' | undefined} buttonIconType - Semantic icon variations used by internal button implementations.
 * @input {boolean} disabled - Whether the action button is disabled.
 * @input {string | undefined} customClass - Optional additional CSS class applied to the component host for custom styling.
 * @input {string | undefined} ariaLabel - Optional ARIA label for accessibility when the visible label is insufficient.
 * @input {DropdownConfig[]} dropdowns - Array of dropdown configuration objects to render additional dropdown controls (each config describes options, labels, and values).
 * @input {boolean} showDropdowns - Toggle for rendering configured dropdowns.
 * @output {EventEmitter<void>} buttonClick - Emitted when the primary action button is clicked.
 * @output {EventEmitter<{index: number, value: any}>} dropdownChange - Emitted when a dropdown selection changes; payload contains the dropdown index and selected value.
 *
 * Key methods:
 * - onButtonClick(): void - Invokes and emits the buttonClick event when the action button is triggered.
 * - onDropdownChange(index: number, value: any): void - Emits dropdownChange with the index of the changed dropdown and the new value.
 *
 * Content projection:
 * - Accepts templates via ContentChild with template reference names 'buttonContent' for custom button content and 'dropdownContent' for custom dropdown content.
 *
 * Lifecycle hooks:
 * - This component does not implement custom Angular lifecycle hooks directly (e.g., OnInit) in the current implementation; change detection and standard Angular lifecycle behavior apply.
 */
@Component({
  selector: 'app-title-bar',
  standalone: true,
  imports: [CommonModule, AddButtonComponent, SimpleDropdownComponent],
  templateUrl: './title-bar.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./title-bar.component.scss'],
})
export class TitleBarComponent {
  @Input() title = '';
  @Input() description = '';
  @Input() showButton = false;
  @Input() buttonText = '';
  @Input() buttonVariant: 'default' | 'primary' | 'secondary' | 'success' | 'danger' = 'primary';
  @Input() buttonSize: 'small' | 'medium' | 'large' = 'medium';
  @Input() buttonIcon?: string;
  @Input() customIcon?: string;
  @Input() buttonIconType?: 'default' | 'break-settings' | 'reward-settings';
  @Input() disabled = false;
  @Input() customClass?: string;
  @Input() ariaLabel?: string;

  // Dropdown support
  @Input() dropdowns: DropdownConfig[] = [];
  @Input() showDropdowns = false;

  @Output() buttonClick = new EventEmitter<void>();
  @Output() dropdownChange = new EventEmitter<{ index: number; value: any }>();

  // Content projection for custom button content
  @ContentChild('buttonContent') buttonContent?: TemplateRef<any>;

  // Content projection for custom dropdown content
  @ContentChild('dropdownContent') dropdownContent?: TemplateRef<any>;

  onButtonClick(): void {
    this.buttonClick.emit();
  }

  onDropdownChange(index: number, value: any): void {
    this.dropdownChange.emit({ index, value });
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
